import React, { useState, useRef, useCallback } from 'react';
import { useTheme } from '@emotion/react';
import { makeStyles } from '@material-ui/styles';
import { Skeleton } from '@material-ui/lab';
import { useWrapperSize } from '../../../hooks';
import { ExtendedStackedBarChartDataItem, StackedBarChartDataItem, Tooltip } from './@types';
import { ChartTooltip } from './ChartTooltip';
import { measureTextWidth } from './@utils';

const useStyles = makeStyles(({ color }) => ({
  wrapper: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '8px',
    '& svg': {
      display: 'block',
    },
  },
  skeleton: {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
  },
  noData: {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    backgroundColor: color.bg.surface.neutral.tertiary.normal,
  },
  chartText: {
    fontSize: '12px',
    textAnchor: 'middle',
    dominantBaseline: 'central',
    userSelect: 'none',
    pointerEvents: 'none',
  },
}));

type Props = {
  data: StackedBarChartDataItem[];
  hideTooltip?: boolean;
  isLoading: boolean;
  tooltipTitle: string;
  showText?: boolean;
  formatText?: (value: number) => string;
};

export const StackedBarChart = (props: Props) => {
  const { data, isLoading, hideTooltip, tooltipTitle, showText = false, formatText } = props;
  const classes = useStyles();
  const { color } = useTheme();

  const { wrapperRef, width, height } = useWrapperSize();
  const textMeasureRef = useRef<SVGTextElement>(null);

  const [tooltip, setTooltip] = useState<Tooltip | null>(null);

  const denominator = data.reduce<number>((prev, cur) => prev + cur.value, 0);
  const chartData: ExtendedStackedBarChartDataItem[] = data.reduce<ExtendedStackedBarChartDataItem[]>(
    (prev, cur, index) => {
      const prevItem = prev.at(index - 1);

      return [
        ...prev,
        {
          ...cur,
          width: (cur.value / denominator) * width,
          x: prevItem ? prevItem.x + prevItem.width : 0,
        },
      ];
    },
    []
  );

  const handleMouseOver = (data: ExtendedStackedBarChartDataItem) => (event: React.MouseEvent<SVGRectElement>) => {
    const wrapperDOMRect = wrapperRef.current?.getClientRects()?.[0];

    setTooltip({
      left: event.clientX,
      top: wrapperDOMRect ? wrapperDOMRect.y + wrapperDOMRect.height : 0,
      data,
    });
  };

  const handleMouseOut = () => {
    setTooltip(null);
  };

  const tooltipEvents = (data: ExtendedStackedBarChartDataItem) => ({
    onMouseOver: handleMouseOver(data),
    onMouseMove: handleMouseOver(data),
    onMouseOut: handleMouseOut,
  });

  const getFormattedText = useCallback(
    (value: ExtendedStackedBarChartDataItem['value']): string => {
      if (formatText) {
        return formatText(value);
      }
      return value.toString();
    },
    [formatText]
  );

  // 텍스트가 바 안에 표시 가능한지 확인
  const checkShowText = useCallback(
    (item: ExtendedStackedBarChartDataItem): boolean => {
      const text = getFormattedText(item.value);
      const textWidth = measureTextWidth(text, textMeasureRef.current);

      // 텍스트 양 옆에 여유 공간을 고려하여 비교
      return textWidth + 4 < item.width;
    },
    [getFormattedText]
  );

  if (isLoading) {
    return (
      <div className={classes.wrapper} ref={wrapperRef}>
        <Skeleton variant="rect" className={classes.skeleton} />
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className={classes.wrapper} ref={wrapperRef}>
        <div className={classes.noData} />
      </div>
    );
  }

  return (
    <div className={classes.wrapper} ref={wrapperRef}>
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
        {/* 텍스트 측정용 요소 */}
        <text ref={textMeasureRef} className={classes.chartText} x="-1000" y="-1000"></text>
        <g>
          {chartData.map((item) => (
            <React.Fragment key={item.label}>
              <rect x={item.x} width={item.width} height={height} fill={item.color} {...tooltipEvents(item)} />
              {showText && checkShowText(item) && (
                <text
                  className={classes.chartText}
                  x={item.x + item.width / 2}
                  y={height / 2}
                  fill={item.textColor || color.content.on_default_color}
                >
                  {getFormattedText(item.value)}
                </text>
              )}
            </React.Fragment>
          ))}
        </g>
      </svg>

      {!hideTooltip && tooltip && <ChartTooltip title={tooltipTitle} formatValue={formatText} {...tooltip} />}
    </div>
  );
};
